import { Heart, Clock, CheckCircle, XCircle, Shield, AlertCircle, Phone, Mail, RefreshCw, LogOut, FileText, Edit } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { useAuthContext } from '../contexts/AuthContext';
import { AtootbandanLogo } from './AtootbandanLogo';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

interface AdminPendingScreenProps {
  onLogout: () => void;
}

export default function AdminPendingScreen({ onLogout }: AdminPendingScreenProps) {
  const { loadUser, user } = useAuthContext();
  const navigate = useNavigate();

  // Check approval status every 10 seconds
  useEffect(() => {
    const interval = setInterval(async () => {
      await loadUser();
      // If approved, user will be redirected automatically by App.tsx
    }, 10000);

    return () => clearInterval(interval);
  }, [loadUser]);

  // Check current verification status and redirect if needed
  useEffect(() => {
    if (!user) return;
    
    const verificationStatus = user.adminVerification?.status;
    
    // If approved, redirect to app
    if (verificationStatus === 'approved') {
      navigate('/app', { replace: true });
    }
    
    // If rejected, redirect to complete-profile to allow updates
    if (verificationStatus === 'rejected') {
      navigate('/complete-profile', { replace: true });
    }
  }, [user, navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-rose-50 to-pink-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center justify-center mb-4">
          <AtootbandanLogo size="md" showText={true} />
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-2xl shadow-2xl p-8 md:p-12">
          {/* Success Icon */}
          <div className="flex justify-center mb-6">
            <div className="size-20 rounded-full bg-amber-100 flex items-center justify-center">
              <Clock className="size-10 text-amber-600" />
            </div>
          </div>

          {/* Title */}
          <h1 className="text-3xl text-center mb-4 text-gray-900">
            Profile Pending Verification
          </h1>

          {/* Description */}
          <p className="text-center text-gray-600 mb-8 text-lg">
            Thank you for completing your profile. Your profile is currently under review by our admin team.
          </p>

          {/* Status Card */}
          <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-6 mb-8 border-2 border-amber-200">
            <div className="flex items-start gap-4">
              <Shield className="size-6 text-amber-700 mt-1 flex-shrink-0" />
              <div>
                <h3 className="text-lg mb-2 text-gray-900">Verification in Progress</h3>
                <p className="text-gray-700 mb-4">
                  Our admin team is reviewing your profile and documents to ensure authenticity and safety for all our users.
                </p>
                <div className="flex items-center gap-2 text-amber-700">
                  <Clock className="size-5" />
                  <span className="font-medium">Expected Time: 7 Working Days</span>
                </div>
              </div>
            </div>
          </div>

          {/* What Happens Next */}
          <div className="mb-8">
            <h3 className="text-xl mb-4 text-gray-900">What Happens Next?</h3>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="size-8 rounded-full bg-rose-100 flex items-center justify-center flex-shrink-0 mt-1">
                  <CheckCircle className="size-5 text-rose-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Profile Review</p>
                  <p className="text-sm text-gray-600">Our team will verify all your details and documents</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="size-8 rounded-full bg-rose-100 flex items-center justify-center flex-shrink-0 mt-1">
                  <FileText className="size-5 text-rose-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Document Verification</p>
                  <p className="text-sm text-gray-600">Your identity documents will be validated</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="size-8 rounded-full bg-rose-100 flex items-center justify-center flex-shrink-0 mt-1">
                  <Heart className="size-5 text-rose-600 fill-rose-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Email Notification</p>
                  <p className="text-sm text-gray-600">You'll receive an email once your profile is approved or if updates are needed</p>
                </div>
              </div>
            </div>
          </div>

          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-8">
            <p className="text-sm text-blue-800">
              <strong>Note:</strong> Once approved, you'll be able to browse profiles, send interests, and start your journey to find your perfect match. We'll notify you via email as soon as your profile is verified.
            </p>
          </div>

          {/* Logout Button */}
          <div className="flex justify-center">
            <Button
              onClick={onLogout}
              variant="outline"
              className="px-8"
            >
              <LogOut className="size-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-sm text-gray-600">
            Questions? Contact us at{' '}
            <a href="mailto:support@atootbandan.com" className="text-rose-600 hover:underline">
              support@atootbandan.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}